/**
 * Campus Map — Mapbox GL JS
 *
 * Affiche les campus sur une carte et synchronise les markers
 * avec le système de filtres AJAX natif du thème Impact (facet-form).
 *
 * Sync bidirectionnelle :
 *   - Clic marker  → coche la checkbox du filtre campus → déclenche la mise à jour AJAX
 *   - Filtre coché → met à jour l'état actif des markers sur la carte
 *
 * Isolation AJAX :
 *   On surcharge section.replaceChildren() pour préserver le wrapper .collection__map
 *   (et donc la carte Mapbox initialisée) à chaque mise à jour des filtres.
 *   Le thème remplace le HTML des produits, la carte reste intacte.
 */

class CampusMap {
  constructor(container) {
    this.container = container;
    this.token = container.dataset.token;
    this.zoom = parseFloat(container.dataset.zoom) || 12;
    this.filterParam = container.dataset.filterParam; // 'filter.p.m.cmd.mo_campus'
    this.campuses = this.readData();
    this.markers = new Map(); // gid → { marker, el }
    this.map = null;
    this.initialized = false;

    if (!this.token || this.campuses.length === 0) return;

    if (typeof mapboxgl !== 'undefined') {
      this.init();
    } else {
      document.addEventListener('DOMContentLoaded', () => {
        if (typeof mapboxgl !== 'undefined') {
          this.init();
        } else {
          const checkMapbox = setInterval(() => {
            if (typeof mapboxgl !== 'undefined') {
              clearInterval(checkMapbox);
              this.init();
            }
          }, 100);
        }
      });
    }
  }

  /**
   * Lit les données campus depuis le JSON injecté en Liquid.
   */
  readData() {
    const el = document.getElementById('campus-map-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent);
    } catch (e) {
      console.warn('[CampusMap] Erreur de parsing JSON :', e);
      return [];
    }
  }

  /**
   * Centre initial : barycentre des campus.
   */
  getCenter() {
    const valid = this.campuses.filter(c => c.lat && c.lng);
    if (valid.length === 0) return [4.3872, 45.4397]; // Saint-Étienne par défaut
    const avgLng = valid.reduce((s, c) => s + parseFloat(c.lng), 0) / valid.length;
    const avgLat = valid.reduce((s, c) => s + parseFloat(c.lat), 0) / valid.length;
    return [avgLng, avgLat];
  }

  /**
   * Surcharge section.replaceChildren() pour préserver le wrapper .collection__map.
   *
   * Le thème appelle shopifySection.replaceChildren(...nouveauHTML) après chaque filtre AJAX.
   * Cette méthode intercepte l'appel pour :
   *   1. Supprimer le .collection__map du HTML entrant (évite le doublon)
   *   2. Laisser replaceChildren() faire son travail normal sur le reste
   *   3. Réinjecter notre wrapper (carte Mapbox intacte) dans la nouvelle grille
   */
  _patchSectionReplaceChildren() {
    const wrapper = this.container.closest('.collection__map');
    if (!wrapper) return;

    const section = wrapper.closest('[id^="shopify-section-"]');
    if (!section) return;

    // Capturer la méthode prototype originale AVANT de la surcharger
    const original = Element.prototype.replaceChildren.bind(section);

    section.replaceChildren = (...nodes) => {
      // Supprimer les éventuels .collection__map dans le HTML entrant
      nodes.forEach(node => {
        if (node.nodeType !== Node.ELEMENT_NODE) return;
        node.querySelectorAll('.collection__map').forEach(el => el.remove());
      });

      // Appeler le replaceChildren original avec le HTML nettoyé
      original(...nodes);

      // Réinsérer notre wrapper (Mapbox intact) juste avant .collection__body
      // (après .collection__mobile-top et .collection__map-backdrop)
      const newLayout = section.querySelector('.collection__layout');
      if (newLayout) {
        const body = newLayout.querySelector('.collection__body');
        if (body) {
          newLayout.insertBefore(wrapper, body);
        } else {
          newLayout.appendChild(wrapper);
        }
      }

      // Si la popin était ouverte, restaurer le backdrop sur le nouveau DOM
      const wasFullscreen = wrapper.classList.contains('collection__map--fullscreen');
      if (wasFullscreen) {
        const newBackdrop = document.getElementById('map-backdrop');
        if (newBackdrop) newBackdrop.classList.add('collection__map-backdrop--visible');
      }

      // Forcer Mapbox à recalculer ses dimensions après réinsertion dans le DOM
      requestAnimationFrame(() => this.map && this.map.resize());

      // Resync les markers avec l'état des filtres du nouveau DOM
      this.syncMarkersWithFilters();
    };
  }

  /**
   * Initialise la carte Mapbox.
   */
  init() {
    if (this.initialized) return;
    this.initialized = true;

    // Patcher replaceChildren AVANT l'init Mapbox
    // → la carte survit à tous les rechargements AJAX suivants
    this._patchSectionReplaceChildren();

    mapboxgl.accessToken = this.token;

    this.map = new mapboxgl.Map({
      container: this.container,
      style: 'mapbox://styles/mapbox/streets-v12',
      center: this.getCenter(),
      zoom: this.zoom,
    });

    this.map.addControl(new mapboxgl.NavigationControl({ showCompass: false }), 'bottom-right');

    this.map.on('load', () => {
      this.campuses.forEach(campus => this.addMarker(campus));
      this.syncMarkersWithFilters();
      this.fitBoundsToCampuses();
    });

    this._initMobileMapToggle();
    this.observeFilterChanges();
  }

  /**
   * Boutons "Afficher / Fermer la carte" sur mobile.
   * Bascule la classe --fullscreen sur le wrapper et appelle map.resize().
   */
  _initMobileMapToggle() {
    const wrapper = document.getElementById('collection-map-wrapper');
    const showBtn = document.getElementById('show-map-btn');
    const closeBtn = document.getElementById('close-map-btn');

    if (!wrapper || !showBtn || !closeBtn) return;

    const open = () => {
      wrapper.classList.add('collection__map--fullscreen');
      // Chercher le backdrop au moment du clic (survit aux remplacements AJAX)
      const bd = document.getElementById('map-backdrop');
      if (bd) bd.classList.add('collection__map-backdrop--visible');
      document.body.style.overflow = 'hidden';
      requestAnimationFrame(() => this.map && this.map.resize());
    };

    const close = () => {
      wrapper.classList.remove('collection__map--fullscreen');
      const bd = document.getElementById('map-backdrop');
      if (bd) bd.classList.remove('collection__map-backdrop--visible');
      document.body.style.overflow = '';
      requestAnimationFrame(() => this.map && this.map.resize());
    };

    showBtn.addEventListener('click', open);
    closeBtn.addEventListener('click', close);

    // Délégation sur le document — survit aux remplacements AJAX du backdrop
    document.addEventListener('click', (e) => {
      if (e.target && e.target.id === 'map-backdrop') close();
    });
  }

  /**
   * Ajuste le viewport pour englober tous les campus.
   */
  fitBoundsToCampuses() {
    const valid = this.campuses.filter(c => c.lat && c.lng);
    if (valid.length === 0) return;

    if (valid.length === 1) {
      this.map.setCenter([parseFloat(valid[0].lng), parseFloat(valid[0].lat)]);
      this.map.setZoom(this.zoom);
      return;
    }

    const bounds = new mapboxgl.LngLatBounds();
    valid.forEach(c => bounds.extend([parseFloat(c.lng), parseFloat(c.lat)]));

    this.map.fitBounds(bounds, {
      padding: 60,
      maxZoom: 14,
      duration: 0,
    });
  }

  /**
   * Crée un marker Mapbox custom pour un campus.
   */
  addMarker(campus) {
    if (!campus.lat || !campus.lng) return;

    const el = document.createElement('button');
    el.className = 'campus-marker';
    el.setAttribute('type', 'button');
    el.setAttribute('aria-label', campus.name || 'Campus');
    el.dataset.gid = campus.gid;

    el.innerHTML = `
      <span class="campus-marker__dot">
        <svg class="campus-marker__icon" width="22" height="31" viewBox="0 0 22 31" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M10.9652 30.3957C10.7648 30.3957 10.4964 30.3356 10.252 30.0698L10.2413 30.0591C9.93812 29.7413 9.59622 29.2445 9.26501 28.765C9.13813 28.5807 9.01793 28.4057 8.91375 28.2655C6.36686 24.7877 0.403635 16.1895 0.0336875 11.4696C-0.149283 9.13375 0.415655 6.89537 1.66573 4.99488C2.81831 3.24264 4.48908 1.85367 6.49775 0.974877C10.9104 -0.953657 15.8239 0.0293076 19.0132 3.47903C24.553 9.47031 20.7147 16.0652 18.1731 20.4285C16.469 23.356 14.491 26.1473 12.5785 28.8478C12.3034 29.2364 12.0283 29.6251 11.7531 30.0137L11.5581 30.2915L11.2269 30.3623C11.1735 30.3743 11.0787 30.3944 10.9625 30.3944L10.9652 30.3957ZM11.0787 1.74015C9.78587 1.74015 8.46635 2.00993 7.1949 2.56685C4.33683 3.81693 1.4093 6.7765 1.7659 11.3347C1.85271 12.4459 2.72616 16.8773 10.3147 27.2398C10.4296 27.396 10.5578 27.5817 10.694 27.7794C10.7755 27.8969 10.8623 28.0238 10.9505 28.148C11.0213 28.0478 11.0934 27.9463 11.1642 27.8461C13.0553 25.1764 15.0105 22.4171 16.6746 19.5564C19.8118 14.1688 22.0569 9.3274 17.7404 4.65966C15.9748 2.74982 13.5748 1.74148 11.0787 1.74148V1.74015ZM11.0546 16.6716C10.849 16.6716 10.6406 16.6622 10.4283 16.6435C6.95317 16.331 5.32246 13.501 5.27839 11.0035C5.23432 8.51806 6.7141 5.65197 10.0677 5.23127C12.4596 4.93211 14.4977 5.78419 15.6596 7.56982C16.8937 9.4663 16.9337 12.1588 15.7558 14.1153C14.7661 15.7607 13.074 16.6716 11.0533 16.6716H11.0546ZM11.0293 6.90605C10.7955 6.90605 10.5471 6.92208 10.284 6.95413C7.98154 7.24261 6.98389 9.18049 7.0146 10.9728C7.04666 12.7785 8.16184 14.6963 10.5832 14.914C12.2139 15.0609 13.5227 14.4586 14.268 13.2205C15.116 11.8115 15.0893 9.87765 14.2039 8.51672C13.707 7.75279 12.7641 6.90472 11.0279 6.90472L11.0293 6.90605Z" fill="currentColor"/>
        </svg>
      </span>
      <span class="campus-marker__label">${campus.name || ''}</span>
    `;

    el.addEventListener('click', (e) => {
      e.stopPropagation();
      this.onMarkerClick(campus);
    });

    const marker = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
      .setLngLat([parseFloat(campus.lng), parseFloat(campus.lat)])
      .addTo(this.map);

    this.markers.set(campus.gid, { marker, el });
  }

  /**
   * Clic sur un marker : coche/décoche la checkbox correspondante dans le formulaire de filtres.
   */
  onMarkerClick(campus) {
    const checkbox = document.querySelector(
      `[is="facet-form"] input[type="checkbox"][name="${this.filterParam}"][value="${campus.gid}"]`
    );

    if (checkbox) {
      checkbox.checked = !checkbox.checked;
      checkbox.dispatchEvent(new Event('change', { bubbles: true }));
    } else {
      this.navigateWithFilter(campus.gid);
    }
  }

  /**
   * Fallback si la checkbox n'est pas trouvable dans le DOM.
   */
  navigateWithFilter(gid) {
    const url = new URL(window.location.href);
    const current = url.searchParams.getAll(this.filterParam);

    url.searchParams.delete(this.filterParam);

    if (current.includes(gid)) {
      current.filter(v => v !== gid).forEach(v => url.searchParams.append(this.filterParam, v));
    } else {
      [...current, gid].forEach(v => url.searchParams.append(this.filterParam, v));
    }

    window.location.href = url.toString();
  }

  /**
   * Lit les GIDs actifs depuis les checkboxes cochées dans le DOM.
   */
  getActiveGids() {
    const checkboxes = document.querySelectorAll(
      `input[type="checkbox"][name="${this.filterParam}"]:checked`
    );
    return new Set([...checkboxes].map(cb => cb.value));
  }

  /**
   * Met à jour l'apparence des markers selon les filtres actifs.
   */
  syncMarkersWithFilters() {
    const activeGids = this.getActiveGids();

    this.markers.forEach(({ el }, gid) => {
      const isActive = activeGids.has(gid);
      el.classList.toggle('campus-marker--active', isActive);
      el.setAttribute('aria-pressed', isActive ? 'true' : 'false');
    });
  }

  /**
   * Observe les mises à jour AJAX du thème pour re-synchroniser les markers.
   * Le patch replaceChildren() gère déjà la resync principale ;
   * ces listeners couvrent les cas alternatifs (popstate, événements custom du thème).
   */
  observeFilterChanges() {
    document.addEventListener('facets:updated', () => this.syncMarkersWithFilters());
    document.addEventListener('collection:reloaded', () => this.syncMarkersWithFilters());

    window.addEventListener('popstate', () => {
      setTimeout(() => this.syncMarkersWithFilters(), 100);
    });

    document.addEventListener('change', (e) => {
      if (
        e.target.name === this.filterParam &&
        e.target.type === 'checkbox' &&
        e.target.closest('[is="facet-form"]')
      ) {
        setTimeout(() => this.syncMarkersWithFilters(), 50);
      }
    });
  }
}

document.addEventListener('DOMContentLoaded', () => {
  const container = document.getElementById('campus-map');
  if (container) {
    new CampusMap(container);
  }
});
